import { Pencil } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@eo/shared';

import { formatHours, stopRowEvent } from './helpers';
import { PlanAddExtraRow } from './PlanAddExtraRow';
import { PlanAddRow } from './PlanAddRow';
import { PlanExtraRow } from './PlanExtraRow';
import { PlanHoursRow } from './PlanHoursRow';
import type { ClientPlanDialogProps } from './types';

// Planning a client lives here so the row only reads. One table: the client's
// hours, then who holds them, each figure being the field that changes it.
export function ClientPlanDialog({ row, people, onPlan }: ClientPlanDialogProps) {
  const share = (userId: string) => (fixedHours: number, variableHours: number) =>
    onPlan({ kind: 'share', clientId: row.clientId, userId, fixedHours, variableHours });

  // The card links to the client, and React carries the portalled dialog's
  // events to it, the click outside that closes the plan included.
  return (
    <span className="contents" onClick={stopRowEvent} onKeyDown={stopRowEvent}>
      <Dialog>
        <DialogTrigger
          aria-label={`Plan ${row.name}`}
          className="shrink-0 rounded-8 p-1 text-text-tertiary transition-colors hover:bg-foreground/[0.06] hover:text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus"
        >
          <Pencil className="size-3.5" />
        </DialogTrigger>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Plan {row.name}</DialogTitle>
            <DialogDescription>
              Hours count from this month on; earlier months keep theirs.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-[minmax(0,1fr)_7rem_7rem_auto] items-center gap-x-3 gap-y-2">
            <span />
            <span className="text-right text-xs text-text-tertiary">Fixed</span>
            <span className="text-right text-xs text-text-tertiary">Variable</span>
            <span />

            <PlanHoursRow
              label="Engagement"
              name={row.name}
              fixed={row.plan.fixed}
              variable={row.plan.variable}
              onSave={(fixedHours, variableHours) =>
                onPlan({ kind: 'hours', clientId: row.clientId, fixedHours, variableHours })
              }
            />

            <span className="col-span-4 mt-2 border-t border-border-faint pt-3 text-xs text-text-tertiary">
              Extra this month
            </span>
            {row.plan.extras.map((extra) => (
              <PlanExtraRow
                key={extra.id}
                extra={extra}
                clientName={row.name}
                onRemove={() =>
                  void onPlan({ kind: 'unextra', supplementId: extra.id }).catch(() => {})
                }
              />
            ))}
            <PlanAddExtraRow
              clientName={row.name}
              onAdd={(extra) => onPlan({ kind: 'extra', clientId: row.clientId, ...extra })}
            />

            <span className="col-span-4 mt-2 border-t border-border-faint pt-3 text-xs text-text-tertiary">
              Who holds them
            </span>
            {row.plan.team.map((s) => (
              <PlanHoursRow
                key={s.userId}
                label={s.name}
                name={s.name}
                fixed={s.fixedHours}
                variable={s.variableHours}
                onSave={share(s.userId)}
                remove={{
                  label: `Remove ${s.name} from ${row.name}`,
                  onClick: () =>
                    void onPlan({ kind: 'unshare', assignmentId: s.assignmentId }).catch(() => {}),
                }}
              />
            ))}
            <div data-testid="plan-cfo-keeps" className="contents">
              <span className="text-sm text-text-tertiary">CFO keeps</span>
              <span className="pr-8 text-right text-sm tabular-nums text-text-secondary">
                {formatHours(row.plan.cfoFixed)}
              </span>
              <span className="pr-8 text-right text-sm tabular-nums text-text-secondary">
                {formatHours(row.plan.cfoVariable)}
              </span>
              <span />
            </div>

            <span className="col-span-4 mt-2 border-t border-border-faint" />
            <PlanAddRow
              clientName={row.name}
              people={people}
              onAdd={(s) => onPlan({ kind: 'share', clientId: row.clientId, ...s })}
            />
          </div>
        </DialogContent>
      </Dialog>
    </span>
  );
}
